import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { vetsApi, Vet } from '../../api/vets';
import { Stethoscope, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface VetAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (vetId: string) => void;
  isPending: boolean;
  currentVetId?: string | null;
}

export function VetAssignmentModal({
  isOpen,
  onClose,
  onAssign,
  isPending,
  currentVetId,
}: VetAssignmentModalProps) {
  const [selectedVetId, setSelectedVetId] = useState<string>('');

  const { data: vets, isLoading } = useQuery<Vet[]>({
    queryKey: ['vets'],
    queryFn: () => vetsApi.list(true),
    enabled: isOpen,
  });

  const handleSubmit = () => {
    if (selectedVetId) {
      onAssign(selectedVetId);
      setSelectedVetId('');
    }
  };

  const handleClose = () => {
    setSelectedVetId('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assegna Veterinario</DialogTitle>
          <DialogDescription>
            Seleziona un veterinario da assegnare a questo caso.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="text-center py-4 text-muted-foreground">
            Caricamento veterinari...
          </div>
        ) : vets && vets.length > 0 ? (
          <div className="space-y-3">
            <Label>Seleziona veterinario</Label>
            <Select value={selectedVetId} onValueChange={setSelectedVetId} disabled={isPending}>
              <SelectTrigger aria-label="Seleziona veterinario">
                <SelectValue placeholder="-- Seleziona --" />
              </SelectTrigger>
              <SelectContent>
                {vets.map((vet) => (
                  <SelectItem key={vet.id} value={vet.id}>
                    <span className="flex items-center gap-2">
                      <Stethoscope className="size-3.5 text-muted-foreground" />
                      {vet.full_name}
                      {vet.specialties.length > 0 && ` (${vet.specialties.join(', ')})`}
                      {vet.id === currentVetId && ' ✓'}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {currentVetId && (
              <p className="text-xs text-muted-foreground">
                Veterinario attuale: {vets.find((v) => v.id === currentVetId)?.full_name || 'N/D'}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            Nessun veterinario disponibile
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedVetId || isPending}>
            <Check className="size-3.5" />
            {isPending ? 'Assegnazione...' : 'Assegna'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
